// src/components/LandingPage/Hero.js
import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { Button } from 'primereact/button';
import { useNavigate } from 'react-router-dom';
import LottiePlayer from '../../../components/media/lottiePlayer';
import { useSetRole } from '../../../services/api/users-api';
import { useUser } from '../../../services/contexts/user-context';

const sparkle = keyframes`
  0%, 100% { opacity: 1; transform: scale(1) rotate(0deg);}
  50% { opacity: 0.6; transform: scale(1.2) rotate(20deg);}
`;

const HeroSection = styled.section`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5rem;
  padding: 6rem 4rem;
  max-width: 1400px;
  margin: 0 auto;

  h2 {
    font-size: 3.5rem;
    font-weight: 900;
    margin-bottom: 1.5rem;
    color: ${({ theme }) => theme.text};
    line-height: 1.2;
    letter-spacing: -0.02em;
  }

  p {
    font-size: 1.2rem;
    margin-bottom: 2.5rem;
    color: ${({ theme }) => theme.text}dd;
    line-height: 1.8;
  }

  .hero-text {
    max-width: 35vw;
    text-align: left;
    flex: 1;
  }

  .highlight-ai {
    font-weight: 800;
    font-size: 1.3rem;
    background: linear-gradient(135deg, #58a6ff 0%, #79c0ff 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
  }

  .sparkle-icon {
    width: 20px;
    height: 20px;
    animation: ${sparkle} 2s infinite;
    transform-origin: center;
    fill: #58a6ff;
  }

  .cta-buttons {
    display: flex;
    gap: 1.5rem;
    flex-wrap: wrap;
    margin-bottom: 1.5rem;

    .p-button {
      font-weight: 600;
      font-size: 1rem;
      padding: 0.75rem 2rem;
      border-radius: 50px;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      box-shadow: 0 4px 15px rgba(88, 166, 255, 0.2);

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 25px rgba(88, 166, 255, 0.3);
      }
    }
  }

  .lottie-container {
    width: 35vw;
    max-width: 100%;
    flex: 1;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    padding: 3rem 2rem;
    gap: 2rem;

    h2 {
      font-size: 2.5rem;
    }

    p {
      font-size: 1.1rem;
    }

    .hero-text {
      max-width: 100%;
      text-align: center;
    }

    .lottie-container {
      width: 300px;
      margin: 2rem auto 0;
    }

    .cta-buttons {
      justify-content: center;
    }
  }
`;

const Hero = ({ isAuthenticated = false }) => {
  const [selectedAudience, setSelectedAudience] = useState(null);
  const navigate = useNavigate();
  const { user } = useUser();
  const { mutate: setRole, isPending: roleLoading } = useSetRole();

  useEffect(() => {
    const savedAudience = localStorage.getItem("selected_audience");
    if (savedAudience) {
      setSelectedAudience(savedAudience);
    }
  }, []);
  const content = {
    default: {
      headline: "Build Your Personal Portfolio Effortlessly",
      bioParts: [
        "Use ",
        "AI",
        " to parse your resume or customize from scratch. Choose from a growing library of templates."
      ],
    },
    recruiter: {
      headline: "Find Top Talent with Our Recruiter Tools",
      bio: "Recruiters can explore a pool of talented professionals with beautifully crafted personal portfolios to find the best fit for their team.",
    },
  };

  const handleAudienceClick = (audience) => {
    setSelectedAudience(audience);
    const existing = JSON.parse(localStorage.getItem("user_info")) || {};
    const updated = { ...existing, profile: audience };
    localStorage.setItem("user_info", JSON.stringify(updated));

    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    const targetRole = audience === 'default' ? 'default' : 'recruiter';
    const targetPath = audience === 'default' ? '/default/profile' : '/recruiter/home';

    if (user?.role === targetRole) {
      navigate(targetPath);
      return;
    }

    setRole(targetRole, {
      onSuccess: () => navigate(targetPath),
    });
  };

  const { headline, bio, bioParts } = selectedAudience ? content[selectedAudience] : content.default;

  return (
    <HeroSection>
      <div className="hero-text">
        <h2>{headline}</h2>
        <p>
          {bioParts ? (
            <>
              {bioParts[0]}
              <span className="highlight-ai">
                {bioParts[1]} <i className="pi pi-sparkles" />
              </span>
              {bioParts[2]}
            </>
          ) : (
            bio
          )}
        </p>

        <div className="cta-buttons">
          <Button
            label={
              isAuthenticated && selectedAudience === 'default'
                ? 'Go to Dashboard'
                : selectedAudience === 'default'
                ? 'Get Started'
                : 'Personal Portfolio'
            }
            icon={isAuthenticated && selectedAudience === 'default' ? 'pi pi-arrow-right' : 'pi pi-user'}
            className={`p-button-rounded ${selectedAudience === 'default' ? 'p-button-success' : 'p-button-outlined p-button-success'}`}
            style={{ minWidth: '180px' }}
            loading={roleLoading && user?.role !== 'default'}
            onClick={() => handleAudienceClick('default')}
          />
          <Button
            label={
              isAuthenticated && selectedAudience === 'recruiter'
                ? 'Go to Recruiter Dashboard'
                : selectedAudience === 'recruiter'
                ? 'Get Started as Recruiter'
                : 'Recruiter'
            }
            icon={isAuthenticated && selectedAudience === 'recruiter' ? 'pi pi-arrow-right' : 'pi pi-briefcase'}
            className={`p-button-rounded ${selectedAudience === 'recruiter' ? 'p-button-info' : 'p-button-outlined p-button-info'}`}
            style={{ minWidth: '180px' }}
            loading={roleLoading && user?.role !== 'recruiter'}
            onClick={() => handleAudienceClick('recruiter')}
          />
        </div>
      </div>

      <div className="lottie-container">
        <LottiePlayer file="https://lottie.host/31651f25-6ce6-4d05-bec6-fd9b174166ff/NDwvyy1hNU.json" />
      </div>
    </HeroSection>
  );
};

export default Hero;
